import React, { useEffect, useRef, useState } from "react";
import { useTypedSelector } from "store";
import styled from "styled-components";
import { api } from "api/api";
import {
  productRegionOptions,
  ProductRegion,
  GetProductParams,
} from "store/reducers/products/products.types";
import { CircularProgressWithLabel } from "components";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import FileOpenOutlinedIcon from "@mui/icons-material/FileOpenOutlined";
import { ImportConflictAction } from "types/misc";
import { ProcessState } from "types/process";
import { useInterval } from "hooks/useInterval";
import { AxiosResponse } from "axios";

//Interval between validation status polling requests in milliseconds
const POLLING_INTERVAL = 1000;

type ConflictStatus = "idle" | "checking" | "no-conflict" | "conflict";

const getProductline = async (productlineId: string): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await api.getProductLine(
      productlineId,
      false
    );

    // If there's no response at all, or it's an error status, treat as not found.
    if (!response || response.status < 200 || response.status >= 300) {
      return null;
    }

    // Only set when we have a successful response with real data
    return response.data ?? null;
  } catch (error) {
    console.error("API error fetching productline: ", error);
    return null;
  }
};

const doesFamilyIdExist = async (familyId: string): Promise<boolean> => {
  try {
    const getProductsFilter: GetProductParams = { familyId: familyId };
    const response: AxiosResponse<any> = await api.getProducts(
      getProductsFilter
    );
    //console.log("get products for familyID", response);

    // If there's no response at all, or it's an error status, treat as not found.
    if (!response || response.status < 200 || response.status >= 300) {
      return false;
    }

    // Only set when we have a successful response with real data
    return (response.data?.total ?? 0) > 0;
  } catch (error) {
    console.error("API error fetching productlines with familyId: ", error);
    return false;
  }
};

export const ImportProductForm = ({
  handleRefreshProductlines,
  visible,
}: {
  handleRefreshProductlines: () => void;
  visible: boolean;
}) => {
  //Redux Store
  const currentUserId = useTypedSelector((state) => state.user.id);

  //State
  const [importFile, setImportFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [fileJson, setFileJson] = useState<Record<string, unknown> | null>(
    null
  );
  const [fileName, setFileName] = useState<string | null>("");
  const [name, setName] = useState<string>("");
  const [region, setRegion] = useState<string>(ProductRegion.GLOBAL);
  const [productlineId, setProductlineId] = useState<string>("");
  const [familyId, setFamilyId] = useState<string>("");
  const [familyIdExists, setFamilyIdExists] = useState<boolean>(false);
  const [existingProductLine, setExistingProductLine] = useState<any>(null);
  const [conflictStatus, setConflictStatus] = useState<ConflictStatus>("idle");
  const [conflictAction, setConflictAction] = useState<ImportConflictAction>(
    ImportConflictAction.Abort
  );
  const [haveReplaceRights, setHaveReplaceRights] = useState<boolean>(false);
  const [processId, setProcessId] = useState<string>("");
  const [processMsg, setProcessMessage] = useState<string>("");
  const [processPercentComplete, setProcessPercentComplete] =
    useState<number>(0);
  const [processState, setProcessState] = useState<ProcessState>(
    ProcessState.IDLE
  );
  const [pollingInterval] = useState<number>(POLLING_INTERVAL);
  const waitingForPollResponse = useRef(false);

  const clearProductLineInfo = () => {
    setFileJson(null);
    setProductlineId("");
    setFamilyId("");
    setFamilyIdExists(false);
    setName("");
    setRegion(ProductRegion.GLOBAL);
    setConflictAction(ImportConflictAction.Abort);
    setConflictStatus("idle");
    setExistingProductLine(null);
    setHaveReplaceRights(false);
    setProcessId("");
    setProcessState(ProcessState.IDLE);
    setProcessPercentComplete(0);
    setProcessMessage("");
  };

  const resetAllState = () => {
    setConflictAction(ImportConflictAction.Abort);
    setConflictStatus("idle");
    setExistingProductLine(null);
    setFileError(null);
    setFileJson(null);
    setFileName("");
    setHaveReplaceRights(false);
    setImportFile(null);
    setName("");
    setProcessId("");
    setProcessMessage("");
    setProcessPercentComplete(0);
    setProcessState(ProcessState.IDLE);
    setProductlineId("");
    setFamilyId("");
    setFamilyIdExists(false);
    setRegion(ProductRegion.GLOBAL);
    clearProductLineInfo();

    waitingForPollResponse.current = false;
  };

  //This useEffect on the change of the productLineId, checks to see
  //if the productline already exists and sets appropriate state variable
  useEffect(() => {
    if (!productlineId) {
      setExistingProductLine(null);
      setConflictStatus("idle");
      return;
    }

    const fetchData = async () => {
      setConflictStatus("checking");

      const productline = await getProductline(productlineId);

      setExistingProductLine(productline);

      if (productline) {
        setConflictStatus("conflict");
      } else {
        setConflictStatus("no-conflict");
      }
    };

    fetchData();
  }, [productlineId]);

  useEffect(() => {
    if (!familyId) {
      setFamilyIdExists(false);
      return;
    }

    const checkFamily = async () => {
      const exists = await doesFamilyIdExist(familyId);
      setFamilyIdExists(exists);
    };

    checkFamily();
  }, [familyId]);

  useEffect(() => {
    const isAdmin = haveAdminRightsToExistingProductLine();
    setHaveReplaceRights(isAdmin);
  }, [existingProductLine]);

  useEffect(() => {
    if (!visible) {
      resetAllState();
    }
  }, [visible]);

  const haveAdminRightsToExistingProductLine = () => {
    if (!existingProductLine || !existingProductLine.users) return false;
    if (!currentUserId) return false;

    const isAdmin = existingProductLine.users.some(
      (user) =>
        user.id === currentUserId.toString().trim() && user.role === "ADMIN"
    );
    return isAdmin;
  };

  // handlers
  const handleFileChange = (event) => {
    //Clear out any previous productline data
    clearProductLineInfo();

    //Get a reference to the file object from the input component
    const file = event.target.files && event.target.files[0];
    if (!file) {
      setImportFile(null);
      setFileName("");
      setFileError("No import file chosen");
      return;
    }

    //reset file input
    event.target.value = null;

    //Save the file to state
    setImportFile(file);
    setFileName(file.name);

    handleFileChosen(file);
  };

  const handleFileChosen = (file: File | null) => {
    if (!file) {
      setFileError("No import file chosen");
      clearProductLineInfo();
      return;
    }
    if (!file.type.match("json")) {
      setFileError("Import file is not valid JSON.");
      clearProductLineInfo();
      return;
    }
    //Case insensitive match for rules database file name suffix
    if (!file.name || !/@@Database.*\.json/i.test(file.name)) {
      setFileError("Not a rules database file");
      clearProductLineInfo();
      return;
    }

    const fileReader = new FileReader();
    fileReader.onloadend = handleFileRead;
    fileReader.readAsText(file);
  };

  const handleFileRead = (e: ProgressEvent<FileReader>) => {
    const content = e.target?.result as string;
    let parsedData: any = null;
    try {
      parsedData = JSON.parse(content);
      setFileJson(parsedData);
      setFileError(null);
    } catch (err) {
      setFileError("Import file is not valid JSON.");
      clearProductLineInfo();
      return;
    }

    if (!parsedData?.ProductLine?.Name) {
      setFileError(
        "Unable to determine the productline name.  This is not a valid file for import."
      );
      clearProductLineInfo();
      return;
    }
    if (!parsedData?.ProductLine?.Id) {
      setFileError(
        "Unable to determine the productline Id.  This is not a valid file for import."
      );
      clearProductLineInfo();
      return;
    }
    if (!parsedData?.ProductLine?.Region) {
      setFileError(
        "Unable to determine the productline Region.  This is not a valid file for import."
      );
      clearProductLineInfo();
      return;
    }

    //Read the object to extract productline info from it and save to state.
    setProductlineId(parsedData?.ProductLine?.Id);
    setFamilyId(parsedData?.ProductLine?.FamilyId);
    setName(parsedData?.ProductLine?.Name);
    setRegion(parsedData?.ProductLine?.Region);
  };

  const handleNameChange = (event) => {
    setName(event.target.value);
  };

  const handleRegionChange = (event) => {
    setRegion(event.target.value);
  };

  const handleConflictActionChange = (event) => {
    setConflictAction(event.target.value);
  };

  //Request API to begin import of the productline
  const beginImport = async () => {
    setProcessState(ProcessState.INITIALIZING);
    setProcessMessage("Uploading productline file to server");

    try {
      //Request to import the productline.
      //This will queue the request to run asynchronously and immediately return
      const response: any = await api.importProductLineDatabaseJson(
        importFile,
        name,
        region,
        conflictAction
      );

      //If the validation request was accepted successfully by the api,
      //update our state with the process response.
      if (response && response.status == 202) {
        if (response.data) {
          setProcessId(response.data.id);
          setProcessState(response.data.processState);
          setProcessPercentComplete(response.data.percentComplete);
          setProcessMessage(response.data.message);
        }
      } else {
        //some error happened, update state with the error message
        setProcessState(ProcessState.ERROR);
        setProcessPercentComplete(100);
        setProcessMessage(
          response?.response?.data
            ? response.response.data
            : "Unable to initiate the Import Productline process."
        );
      }
    } catch (error) {
      //A totally unexpected error happened.  Update state.
      console.error(
        error,
        "Error occurred while starting importing productline"
      );
      setProcessState(ProcessState.ERROR);
      setProcessPercentComplete(100);
      setProcessMessage("Error occurred while starting importing productline");
    }
  };

  const pollImportStatus = async () => {
    //Begin polling request if we have a process we are tracking and we are not already
    //waiting on a response.
    //console.log("pollImportStatus BEGIN");
    if (processId && !waitingForPollResponse.current) {
      //flag that we are waiting to prevent re-entrance
      waitingForPollResponse.current = true;

      try {
        const response = await api.getProcess(processId);

        if (response.status != 200) {
          setProcessState(ProcessState.ERROR);
        }

        if (response && response.data && response.data.processState) {
          setProcessState(response.data.processState);
          setProcessPercentComplete(response.data.percentComplete ?? 0);
          setProcessMessage(response.data.message);

          //If the import process is complete, stop polling, and
          //trigger the callback to refresh the productlines
          if (response.data.processState == ProcessState.COMPLETE) {
            handleRefreshProductlines();
          }
        } else {
          //some error happened, update state with the error message
          setProcessState(ProcessState.ERROR);
          setProcessPercentComplete(100);
          setProcessMessage(
            response?.data?.message
              ? response.data.message
              : "Unable to retrieve status of the Import Productline process."
          );
        }
      } catch (error) {
        console.error(error, "Error occurred while importing productline");
        setProcessState(ProcessState.ERROR);
        setProcessPercentComplete(100);
        setProcessMessage("Error occurred while importing productline");
      } finally {
        waitingForPollResponse.current = false;
        //console.log("pollImportStatus END");
      }
    }
  };

  //While the import process is queued or actively processing,
  //we will periodically poll the process status.
  //If the import is not processing, we will stop polling by setting
  //the delay/interval to 0.
  useInterval(
    pollImportStatus,
    processState === ProcessState.QUEUED ||
      processState === ProcessState.PROCESSING
      ? pollingInterval
      : 0
  );

  //If the form is not visible, don't bother rendering anything
  if (!visible) {
    return null;
  }

  const idMatch: boolean = existingProductLine?.id === productlineId;

  const familyIdMatch: boolean = existingProductLine?.familyId === familyId;

  const canReplace = haveReplaceRights && idMatch && familyIdMatch;

  const canImportAsNewVersion = familyId && familyIdExists;

  //Determine if we have gathered all the items necessary to do the import
  const isConflictResolved =
    conflictStatus === "no-conflict" ||
    (conflictStatus === "conflict" &&
      conflictAction !== ImportConflictAction.Abort);

  const isImportReady =
    processState === ProcessState.IDLE &&
    !!fileJson &&
    !!name &&
    !!region &&
    conflictStatus !== "idle" &&
    conflictStatus !== "checking" &&
    isConflictResolved;

  return (
    <Grid
      container
      columnSpacing={1}
      rowSpacing={3}
      alignItems="center"
      style={{ marginLeft: "20px", marginRight: "20px" }}
    >
      <Grid xs={10}>
        <TextField
          label="File Name"
          value={fileName}
          id="file-text-input"
          variant="outlined"
          size="small"
          fullWidth
          multiline
          margin="none"
          disabled
        />
      </Grid>
      <Grid xs>
        <Button component="label" variant="contained">
          <FileOpenOutlinedIcon />
          <VisuallyHiddenInput
            type="file"
            accept=".json"
            onChange={handleFileChange}
          />
        </Button>
      </Grid>
      {fileError && (
        <Grid xs={12}>
          <Alert severity="error">{fileError}</Alert>
        </Grid>
      )}
      <Grid xs={12}>
        <TextField
          label="Productline Name"
          value={name}
          id="productline-name"
          variant="outlined"
          size="small"
          fullWidth
          margin="none"
          onChange={handleNameChange}
          disabled={!fileJson}
        />
      </Grid>
      <Grid xs={12}>
        <TextField
          label="Region"
          //defaultValue={ProductRegion.GLOBAL}
          value={region}
          id="productline-region"
          variant="outlined"
          size="small"
          fullWidth
          margin="none"
          select
          SelectProps={{
            native: true,
          }}
          onChange={handleRegionChange}
          disabled={!fileJson}
        >
          {productRegionOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </TextField>
      </Grid>
      <Grid xs={12}>
        <TextField
          label="Productline Id"
          id="productline-id"
          value={productlineId}
          variant="outlined"
          size="small"
          fullWidth
          margin="none"
          disabled
        />
      </Grid>
      <Grid xs={12}>
        <TextField
          label="Productline Family Id"
          id="productline-familyid"
          value={familyId ?? "None - Will import as new family"}
          variant="outlined"
          size="small"
          fullWidth
          margin="none"
          disabled
        />
      </Grid>

      {conflictStatus === "checking" && (
        <Grid xs={12}>
          <Typography variant="body2">
            Checking for existing productline...
          </Typography>
        </Grid>
      )}

      {existingProductLine && (
        <Grid
          xs={12}
          sx={{ border: "2px solid orange" }}
          justifyContent="center"
        >
          <Alert severity="warning">
            <AlertTitle>Productline Conflict</AlertTitle>

            <Typography variant="body2">
              A productline with the same Id already exists:
            </Typography>

            <Stack spacing={0.5} mt={1}>
              <Typography variant="body2">
                <strong>Name:</strong> {existingProductLine.name}
              </Typography>
              <Typography variant="body2">
                <strong>Id:</strong> {existingProductLine.id}
              </Typography>
              <Typography variant="body2">
                <strong>Family Id:</strong> {existingProductLine.familyId}
              </Typography>
            </Stack>
          </Alert>
          <FormControl sx={{ ml: 2, mt: 2 }} fullWidth>
            <FormLabel id="conflict-actions-group-label">
              <Typography variant="subtitle1">
                How should the conflict be resolved?
              </Typography>
            </FormLabel>

            <RadioGroup
              aria-labelledby="conflict-actions-group-label"
              value={conflictAction}
              name="conflict-actions-radio-group"
              onChange={handleConflictActionChange}
            >
              <Stack spacing={1}>
                <FormControlLabel
                  value={ImportConflictAction.Abort}
                  control={<Radio />}
                  label="Do not import"
                />

                <FormControlLabel
                  value={ImportConflictAction.Replace}
                  control={<Radio />}
                  disabled={!canReplace}
                  label={
                    <Box>
                      <Typography variant="body1">
                        Replace existing Productline
                      </Typography>

                      {!canReplace && (
                        <FormHelperText
                          sx={{
                            m: 0,
                            ml: 1,
                            color: "text.disabled",
                          }}
                        >
                          <Stack spacing={0.25}>
                            {!haveReplaceRights && (
                              <Typography variant="caption" color="inherit">
                                Unavailable: ADMIN rights required
                              </Typography>
                            )}
                            {productlineId !== existingProductLine.id && (
                              <Typography variant="caption" color="inherit">
                                Unavailable: Productline Id does not match
                              </Typography>
                            )}
                            {familyId !== existingProductLine.familyId && (
                              <Typography variant="caption" color="inherit">
                                Unavailable: Family Id does not match
                              </Typography>
                            )}
                          </Stack>
                        </FormHelperText>
                      )}
                    </Box>
                  }
                />

                <FormControlLabel
                  value={ImportConflictAction.NewVersion}
                  control={<Radio />}
                  disabled={!canImportAsNewVersion}
                  label={
                    <Box>
                      <Typography variant="body1">
                        Import as new version in Productline Family
                      </Typography>

                      {!canImportAsNewVersion && (
                        <FormHelperText
                          sx={{
                            m: 0,
                            ml: 1,
                            color: "text.disabled",
                          }}
                        >
                          <Stack spacing={0.25}>
                            {(!familyId || !familyIdExists) && (
                              <Typography variant="caption">
                                Unavailable: Import Productline&apos;s Family
                                does not exist
                              </Typography>
                            )}
                          </Stack>
                        </FormHelperText>
                      )}
                    </Box>
                  }
                />

                <FormControlLabel
                  value={ImportConflictAction.NewFamily}
                  control={<Radio />}
                  label="Import as new Productline & Family"
                />
              </Stack>
            </RadioGroup>
          </FormControl>
        </Grid>
      )}
      <Grid xs={12}>
        <Button
          id="productline-import"
          variant="contained"
          size="large"
          fullWidth
          disabled={!isImportReady}
          onClick={beginImport}
        >
          Import Productline
        </Button>
      </Grid>
      <Grid xs={12} display="flex" justifyContent="center">
        {processState === ProcessState.INITIALIZING && <CircularProgress />}
        {(processState === ProcessState.QUEUED ||
          processState === ProcessState.PROCESSING ||
          processState === ProcessState.COMPLETE) && (
          <CircularProgressWithLabel value={processPercentComplete} />
        )}
      </Grid>
      <Grid xs={12} display="flex" justifyContent="center">
        {(processState === ProcessState.INITIALIZING ||
          processState === ProcessState.QUEUED ||
          processState === ProcessState.PROCESSING) && (
          <Alert severity="info" variant="outlined">
            <strong>Import Progress</strong>
            <p>{processMsg}</p>
          </Alert>
        )}
        {processState === ProcessState.COMPLETE && (
          <Alert severity="success" variant="outlined">
            <strong>Success</strong>
            <p>{processMsg}</p>
          </Alert>
        )}
        {processState === ProcessState.ERROR && (
          <Alert severity="error" variant="outlined">
            <strong>Error: Import Failed</strong>
            <p>{processMsg}</p>
          </Alert>
        )}
      </Grid>
    </Grid>
  );
};

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export default ImportProductForm;
